import { ANLoadingScreen } from "@answersnow/pkg-theme";
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  ListItemIcon,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { CaretDown, X } from "@phosphor-icons/react";
import { useGetAssignableClinicians } from "hooks/api/clinicians/useGetAssignableClinicians";
import React, { useState } from "react";

interface AssignClinicianProps {
  open: boolean;
  assignedRole: string | undefined;
  onCloseAssignClinician: () => void;
  onOpenAssignClinicianConfirm: (clinician: any) => void;
  clinician: any;
  secondaryClinician: any;
}

function AssignClinician({
  open,
  assignedRole,
  onCloseAssignClinician,
  onOpenAssignClinicianConfirm,
  clinician,
  secondaryClinician,
}: AssignClinicianProps) {
  const [selectedClinician, setSelectedClinician] = useState(null);
  const assignmentType = assignedRole === "secondary" ? "Supervisor" : "Clinician";

  const { assignableClinicians, assignableCliniciansLoading } = useGetAssignableClinicians();
  const filteredAssignableClinicians = assignableClinicians?.filter((c) => {
    if (!!clinician || !!secondaryClinician) {
      return (
        c.clinician_id !== clinician?.clinician_id &&
        c.clinician_id !== secondaryClinician?.clinician_id
      );
    } else {
      return c;
    }
  });

  const handleClose = () => {
    setSelectedClinician(null);
    onCloseAssignClinician();
  };

  const handleConfirm = () => {
    if (selectedClinician) {
      onOpenAssignClinicianConfirm(selectedClinician);
      setSelectedClinician(null);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="assign-clinician-dialog-title"
      maxWidth="xs"
      fullWidth
      PaperProps={{ elevation: 3 }}
    >
      <DialogTitle id="assign-clinician-dialog-title">
        Assign {assignmentType}
        <IconButton aria-label="Close assignment dialog" onClick={handleClose} size="small">
          <X size={20} />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {assignableCliniciansLoading ? (
          <Stack sx={{ height: 100 }}>
            <ANLoadingScreen />
          </Stack>
        ) : filteredAssignableClinicians?.length === 0 ? (
          <Typography variant="body1" sx={{ mt: 1 }}>
            NO ASSIGNABLE CLINICIANS
          </Typography>
        ) : (
          <Stack gap={3}>
            <Typography
              component="label"
              htmlFor="assign-clinician-input"
              variant="caption"
              sx={{ color: "text.secondary", fontWeight: 500, lineHeight: 1 }}
            >
              Search clinicians
            </Typography>
            <Autocomplete
              id="assign-clinician-input"
              options={(filteredAssignableClinicians as any[]) || []}
              getOptionLabel={(option: any) => option?.name || ""}
              isOptionEqualToValue={(option: any, value: any) =>
                option?.clinician_id === value?.clinician_id
              }
              popupIcon={<CaretDown size={20} />}
              onChange={(event, newValue) => {
                setSelectedClinician(newValue);
              }}
              value={selectedClinician}
              renderOption={(props, option: any) => (
                <MenuItem {...props} key={option?.clinician_id}>
                  <ListItemIcon>
                    <Avatar src={option?.image_url} sx={{ width: 24, height: 24 }} />
                  </ListItemIcon>
                  <Typography variant="body2" sx={{ ml: 1 }}>
                    {option?.name}
                    <Box component="span" sx={{ color: "text.secondary" }}>
                      {"  -  "}
                      {option?.is_director
                        ? "Clinical Director"
                        : option?.is_supervisor
                          ? "Clinical Supervisor"
                          : "Clinician"}
                    </Box>
                  </Typography>
                </MenuItem>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Please select"
                  size="small"
                  fullWidth
                  autoFocus
                  inputProps={{
                    ...params.inputProps,
                    "aria-label": "Search clinicians",
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                    },
                  }}
                />
              )}
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button color="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button onClick={handleConfirm} disabled={!selectedClinician} color="primary">
          Assign {assignmentType}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AssignClinician;
